import { RootState, store } from 'modules/Editor/store';
import { selectZoom } from 'modules/Editor/store/editorSettingsSelector';
import { selectWidgets } from 'modules/Editor/store/infographSelector';
import { updateWidget } from 'modules/Editor/store/infographSlice';
import { ActiveWidget } from 'modules/Editor/store/widgetControlSlice';
import { selectActiveWidgets } from 'modules/Editor/store/widgetSelector';
import { WidgetId } from 'types/idTypes';
import { Subset } from 'types/object.types';
import { AllWidgetData, GroupWidgetData } from 'widgets/Widget.types';

import { AlignmentOption, Boundary } from './AlignmentMenu.types';
import { computeAlignmentBoundary, getWidgetBoundary } from './common/WidgetBoundary.helpers';

/**
 * Align widgets in the specified direction
 * Widgets are aligned to the specified boundary
 *
 * @param direction
 * @param activeWidgets
 * @param boundary
 */
const alignWidgetsByDirection = (direction: AlignmentOption, activeWidgets: ActiveWidget[], boundary: Boundary) => {
  const storeState = store.getState() as RootState;
  const widgets = selectWidgets(storeState);
  const zoomLevel = selectZoom(storeState);
  const dispatch = store.dispatch;

  const widgetUpdates: { widgetId: WidgetId; widgetData: Subset<AllWidgetData> }[] = [];

  const isHorizontalAlign = [AlignmentOption.Left, AlignmentOption.Right, AlignmentOption.Center].includes(direction);

  const widgetDataPropToUpdate = isHorizontalAlign ? 'leftPx' : 'topPx';

  // The function used to compute the delta to shift the widget
  let deltaFn: (widgetBoundary: Boundary) => number = () => 0;
  switch (direction) {
    case AlignmentOption.Top:
      deltaFn = (widgetBoundary) => boundary.top - widgetBoundary.top;
      break;
    case AlignmentOption.Bottom:
      deltaFn = (widgetBoundary) => boundary.bottom - widgetBoundary.bottom;
      break;
    case AlignmentOption.Left:
      deltaFn = (widgetBoundary) => boundary.left - widgetBoundary.left;
      break;
    case AlignmentOption.Right:
      deltaFn = (widgetBoundary) => boundary.right - widgetBoundary.right;
      break;
    // Middle = vertical center
    case AlignmentOption.Middle:
      deltaFn = (widgetBoundary) => {
        const alignmentCenter = boundary.top + (boundary.bottom - boundary.top) / 2;
        const widgetCenter = widgetBoundary.top + (widgetBoundary.bottom - widgetBoundary.top) / 2;
        return alignmentCenter - widgetCenter;
      };
      break;
    // Center = horizontal center
    case AlignmentOption.Center:
      deltaFn = (widgetBoundary) => {
        const alignmentCenter = boundary.left + (boundary.right - boundary.left) / 2;
        const widgetCenter = widgetBoundary.left + (widgetBoundary.right - widgetBoundary.left) / 2;
        return alignmentCenter - widgetCenter;
      };
      break;
    default:
      break;
  }

  // For each widget, calculate the delta between the widget's boundary and the alignment boundary
  // Update the widget position based on the delta
  for (const widget of activeWidgets) {
    const widgetId = widget.groupId || widget.responsiveGroupId || widget.id;
    const widgetBoundary = getWidgetBoundary(widgetId, widgets, zoomLevel);
    const delta = deltaFn(widgetBoundary);

    // Handle group widget, need to update all widgets in group
    if (!!widget.groupId) {
      const groupWidget = widgets[widget.groupId] as GroupWidgetData;
      const memberWidgetIds = groupWidget.memberWidgetIds;

      for (const memberWidgetId of memberWidgetIds) {
        const memberWidget = widgets[memberWidgetId];
        widgetUpdates.push({
          widgetId: memberWidgetId,
          widgetData: {
            [widgetDataPropToUpdate]: memberWidget[widgetDataPropToUpdate] + delta,
          },
        });
      }
      continue;
    }

    const widgetData = widgets[widgetId];
    widgetUpdates.push({
      widgetId,
      widgetData: { [widgetDataPropToUpdate]: widgetData[widgetDataPropToUpdate] + delta },
    });
  }

  dispatch(updateWidget(widgetUpdates));
};

/**
 * Align the active widgets in the specified direction
 * If a single widget is selected, it will be aligned to the page bounds
 * If multiple widgets are selected, it will be aligned to the multiselect bounding box
 * @param direction
 */
const alignWidgets = (direction: AlignmentOption) => {
  const storeState = store.getState() as RootState;

  const activeWidgets = selectActiveWidgets(storeState);

  const boundary = computeAlignmentBoundary(activeWidgets);
  alignWidgetsByDirection(direction, activeWidgets, boundary);
};

export { alignWidgets };
